import { IRequestType } from '@interfaces/request';
import Router from 'next/router';
import React from 'react';
import { theme } from '../../../style/global-style';
import { formatDepartureReturn } from '../../../utils/date';
import { formatItineraryInfo, formatNumberOfNights } from '../../../utils/dreamlines-formatter';
import { eventTracker } from '../../../utils/EventTracker';
// import { cabinMapper } from '../../../utils/mappings';
import { __ } from '../../../utils/translation-service';
import { Card } from '../../atoms/card/card';
import { NextImage } from '../../atoms/image/image';
import { DreamlinesButton } from '../../atoms/dreamlines-button/dreamlines-button';
import { LabelBox } from '../../atoms/label-box/label-box';
import { Rating } from '../../atoms/rating/rating';
import { SimpleGallery } from '../../atoms/simple-gallery/simple-gallery';
import { IOfferTeaserItem } from '../../sections/offer-teaser/offer-teaser';
import { Price } from '../price';
import {
    StyledAspectRatio,
    StyledBottomSection,
    StyledCompanyLogoWrapper,
    StyledContentWrapper,
    StyledDurationInfo,
    StyledEventsInfo,
    StyledFlexSpaceBetween,
    StyledImage,
    StyledImageWrapper,
    StyledButtonWrapper,
    StyledItineraryInfo,
    StyledMarketingLabels,
    StyledMarketingOfferLabel,
    StyledMiddleSection,
    StyledOfferBoardCard,
    StyledOfferHeading,
    StyledTextLabelList,
    StyledTextLabelListItem,
    StyledTopSection,
    StyledUserLove,
    StyledMargin,
    StyledCheckMarkIcon,
    StyledIconList,
    StyledIconListItem,
    StyledFlex,
} from './offer-board-card.style';
import { formatOfferTitle } from './offer-label.utils';

export const OfferBoardCard = (props: IOfferTeaserItem) => {
    const {
        images,
        // cheapestCabinType,
        shipName,
        companyLogo,
        companyName,
        redirectUrl,
        numOfNights,
        numOfDaysAtSea,
        numOfSails,
        numOfPorts,
        ratingValue,
        ratingString,
        ratingCount,
        startHarbour,
        onBoardCredit,
        totalStock,
        departureDate,
        returnDate,
        minPrice,
        discount,
        withFlight,
        textLabelItems,
        visits,
        cabinStockThreshold,
        highDemand,
        icons,
        cardNum,
    } = props;

    const formattedNumberOfNights = formatNumberOfNights(numOfNights);
    const formattedOfferTitle =
        startHarbour &&
        shipName &&
        formatOfferTitle(__('offerTitle'), startHarbour, shipName, formattedNumberOfNights);
    const formattedHighDemand = highDemand && __('highDemand').replace(/\[x]/g, String(visits));
    const cabinsLeftTranslation =
        !!totalStock && totalStock > 1 ? __('cabinsLeft') : __('cabinLeft');

    const formattedCabinsLeft =
        !!totalStock &&
        totalStock < cabinStockThreshold &&
        cabinsLeftTranslation.replace('[totalStock]', String(totalStock));
    const formattedOnBoardCredit =
        !!onBoardCredit && __('OBCLabel').replace('[onBoardCredit]', String(onBoardCredit));

    const nightsPortsDaysAtSea = formatItineraryInfo(
        numOfNights,
        numOfPorts,
        numOfDaysAtSea as number,
    );

    cabinsLeftTranslation.replace('[totalStock]', String(totalStock));

    return (
        <StyledOfferBoardCard>
            <Card>
                <StyledTopSection>
                    {images.length > 1 ? (
                        <SimpleGallery showShadow>
                            {images.map((image, index) => (
                                <StyledImageWrapper key={index}>
                                    <StyledAspectRatio>
                                        <StyledContentWrapper>
                                            <NextImage
                                                imgUrl={image.url}
                                                placeholder={image.url}
                                                width={image.width}
                                                height={image.height}
                                                alt={'An image of ' + shipName + ' ship'}
                                            />
                                        </StyledContentWrapper>
                                    </StyledAspectRatio>
                                </StyledImageWrapper>
                            ))}
                        </SimpleGallery>
                    ) : (
                        <StyledImageWrapper>
                            <StyledAspectRatio>
                                <StyledContentWrapper>
                                    {images[0] && (
                                        <NextImage
                                            imgUrl={images[0].url}
                                            placeholder={images[0].url}
                                            width={images[0].width}
                                            height={images[0].height}
                                            alt={'An image of ' + shipName + ' ship'}
                                        />
                                    )}
                                </StyledContentWrapper>
                            </StyledAspectRatio>
                        </StyledImageWrapper>
                    )}
                    <StyledCompanyLogoWrapper>
                        <StyledImage src={companyLogo} alt={`Logo of ${companyName}`} />
                    </StyledCompanyLogoWrapper>
                </StyledTopSection>

                <StyledMiddleSection>
                    <StyledFlexSpaceBetween>
                        <StyledItineraryInfo>{nightsPortsDaysAtSea}</StyledItineraryInfo>
                        {Boolean(ratingValue) && (
                            <Rating
                                value={ratingValue}
                                string={ratingString}
                                amount={ratingCount}
                            />
                        )}
                    </StyledFlexSpaceBetween>
                    <StyledFlexSpaceBetween>
                        <StyledOfferHeading href={redirectUrl} aria-label={formattedOfferTitle}>
                            {formattedOfferTitle}
                        </StyledOfferHeading>
                    </StyledFlexSpaceBetween>

                    <StyledFlex>
                        <StyledDurationInfo>
                            {formatDepartureReturn(departureDate, returnDate, numOfSails === 1)}
                        </StyledDurationInfo>
                        {numOfSails > 1 && (
                            <StyledEventsInfo>{`${numOfSails} ${__('events')}`}</StyledEventsInfo>
                        )}
                    </StyledFlex>

                    <StyledFlexSpaceBetween>
                        <StyledMarketingLabels>
                            {!!formattedCabinsLeft && (
                                <StyledMarketingOfferLabel color={theme.labels.onlineBookable}>
                                    {formattedCabinsLeft}
                                </StyledMarketingOfferLabel>
                            )}

                            {!!formattedOnBoardCredit && (
                                <StyledMarketingOfferLabel color={theme.colors.discountSpecial}>
                                    {formattedOnBoardCredit}
                                </StyledMarketingOfferLabel>
                            )}

                            {!!formattedHighDemand && (
                                <StyledMarketingOfferLabel color={theme.labels.urgency}>
                                    {formattedHighDemand}
                                </StyledMarketingOfferLabel>
                            )}
                        </StyledMarketingLabels>
                    </StyledFlexSpaceBetween>
                </StyledMiddleSection>

                <StyledBottomSection>
                    <StyledFlexSpaceBetween>
                        <StyledIconList>
                            {icons.map((icon, index) => (
                                <StyledIconListItem key={index}>
                                    <StyledCheckMarkIcon></StyledCheckMarkIcon>
                                    {__(icon.text)}
                                </StyledIconListItem>
                            ))}
                        </StyledIconList>
                    </StyledFlexSpaceBetween>
                    {textLabelItems?.length ? (
                        <div>
                            <StyledUserLove>{__('userLove')}</StyledUserLove>
                            <StyledTextLabelList>
                                {textLabelItems.map((textLabel, index) => {
                                    return (
                                        <StyledTextLabelListItem key={index}>
                                            <LabelBox
                                                type={textLabel.label}
                                                label={__(textLabel.label)}
                                            />
                                        </StyledTextLabelListItem>
                                    );
                                })}
                            </StyledTextLabelList>
                        </div>
                    ) : null}

                    <StyledMargin>
                        <Price
                            price={Number(minPrice.toFixed(0))}
                            discount={discount}
                            withFlight={withFlight}
                            onRequest={!minPrice}
                            size={'S'}
                        />
                    </StyledMargin>

                    <StyledButtonWrapper>
                        <DreamlinesButton
                            primary
                            wrapperStyle={{
                                width: '100%',
                                padding: '0 10px',
                            }}
                            style={{
                                textDecoration: 'none',
                            }}
                            redirectUrl={redirectUrl}
                            onClick={() => {
                                handleClick(props.requestType, props.shipName, cardNum);
                                Router.push(redirectUrl);
                            }}
                            domType="a"
                        >
                            {__('seeOffers')}
                        </DreamlinesButton>
                    </StyledButtonWrapper>
                </StyledBottomSection>
            </Card>
        </StyledOfferBoardCard>
    );
};

const handleClick = (type: IRequestType, title: string, cardNum: number) =>
    eventTracker.track({
        event: 'click',
        category: 'offers-teasers',
        action: `teaser-${cardNum}`,
        label: `${type}-${title}`,
    });
